import { get, post, put, del } from '../client'
import {
  StrategicPillarFilterParamsSchema,
  PaginationParamsSchema,
  CreateStrategicPillarSchema,
  UpdateStrategicPillarSchema,
  type StrategicPillar,
  type StrategicPillarDetail,
  type StrategicPillarFilterParams,
  type CreateStrategicPillar,
  type UpdateStrategicPillar,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams
} from '../types'

export class StrategicPillarService {
  /**
   * Get strategic pillars with pagination and filters
   * POST /strategic-pillar/private/filter
   */
  async getStrategicPillars(
    params: PaginationParams = { page: 1, size: 10 },
    filters: StrategicPillarFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<StrategicPillar>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = StrategicPillarFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<StrategicPillar>>>(
      `strategic-pillar/private/filter?${query}`,
      validatedFilters
    )
  }

  /**
   * Create strategic pillar
   * POST /strategic-pillar/admin/private
   */
  async createStrategicPillar(data: CreateStrategicPillar): Promise<ApiResponse<StrategicPillarDetail>> {
    const validatedData = CreateStrategicPillarSchema.parse(data)

    const payload = {
      title: validatedData.title,
      coverImageTitle: validatedData.coverImageTitle,
      articleTag: validatedData.articleTag,
      landingPagePrioritiesOfAfsa: validatedData.landingPagePrioritiesOfAfsa,
      whoWeAre: validatedData.whoWeAre,
      countries: validatedData.countries,
      history: validatedData.history,
      member: validatedData.member,
      content: validatedData.content,
      status: validatedData.status,
      coverImageUrl: validatedData.coverImageUrl
    }

    return await post<ApiResponse<StrategicPillarDetail>>(
      'strategic-pillar/admin/private',
      payload
    )
  }


  async updateStrategicPillar(
    strategicPillarId: string,
    data: UpdateStrategicPillar
  ): Promise<ApiResponse<null>> {
    const validatedData = UpdateStrategicPillarSchema.parse(data)

    const payload = {
      title: validatedData.title,
      coverImageTitle: validatedData.coverImageTitle,
      articleTag: validatedData.articleTag,
      landingPagePrioritiesOfAfsa: validatedData.landingPagePrioritiesOfAfsa,
      whoWeAre: validatedData.whoWeAre,
      countries: validatedData.countries,
      history: validatedData.history,
      member: validatedData.member,
      content: validatedData.content,
      ...(validatedData.status && { status: validatedData.status }),
      ...(validatedData.coverImageUrl && { coverImageUrl: validatedData.coverImageUrl })
    }

    return await put<ApiResponse<null>>(
      `strategic-pillar/admin/private/${strategicPillarId}`,
      payload
    )
  }

  
  async updateStrategicPillarStatus(strategicPillarId: string, status: 'DRAFT' | 'PUBLISH'): Promise<ApiResponse<StrategicPillar>> {
    return await put<ApiResponse<StrategicPillar>>(
      `strategic-pillar/admin/private/${strategicPillarId}/status`,
      { status }
    )
  }


  async deleteStrategicPillar(strategicPillarId: string): Promise<ApiResponse<null>> {
    return await del<ApiResponse<null>>(
      `strategic-pillar/admin/private/${strategicPillarId}`
    )
  }

  /**
   * Get strategic pillar by ID
   * GET /strategic-pillar/private/{strategicPillarId}
   */
  async getStrategicPillarById(strategicPillarId: string): Promise<ApiResponse<StrategicPillarDetail>> {
    return await get<ApiResponse<StrategicPillarDetail>>(
      `strategic-pillar/private/${strategicPillarId}`
    )
  }

  /**
   * Get total published strategic pillars
   * GET /strategic-pillar/private/stats/published
   */
  async getTotalPublishedStrategicPillars(): Promise<ApiResponse<{ publishedStrategicPillars: number }>> {
    return await get<ApiResponse<{ publishedStrategicPillars: number }>>(
      'strategic-pillar/private/stats/published'
    )
  }

  /**
   * Get total draft strategic pillars
   * GET /strategic-pillar/private/stats/draft
   */
  async getTotalDraftStrategicPillars(): Promise<ApiResponse<{ draftStrategicPillars: number }>> {
    return await get<ApiResponse<{ draftStrategicPillars: number }>>(
      'strategic-pillar/private/stats/draft'
    )
  }
}

export const strategicPillarService = new StrategicPillarService()

export default strategicPillarService
